import { lazy, Suspense, type ComponentType, type ReactElement } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { Shell } from "./components/Layout";
import { AuthGate } from "./features/auth/AuthGate";
import { SignIn } from "./features/auth/SignIn";
import { EmptyState, Loading } from "./components/ui";

/** Code-split each screen; pages are named exports. */
function page<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(() => load().then((m) => ({ default: m[name] })));
}

const HomePage = page(() => import("./features/home/HomePage"), "HomePage");
const CalendarPage = page(() => import("./features/calendar/CalendarPage"), "CalendarPage");
const RegistryPage = page(() => import("./features/registry/RegistryPage"), "RegistryPage");
const OrganisationPage = page(() => import("./features/registry/OrganisationPage"), "OrganisationPage");
const ContactsPage = page(() => import("./features/contacts/ContactsPage"), "ContactsPage");
const ContactPage = page(() => import("./features/contacts/ContactPage"), "ContactPage");
const PipelinePage = page(() => import("./features/pipeline/PipelinePage"), "PipelinePage");
const DealPage = page(() => import("./features/pipeline/DealPage"), "DealPage");
const AccountsPage = page(() => import("./features/accounts/AccountsPage"), "AccountsPage");
const AdminPage = page(() => import("./features/admin/AdminPage"), "AdminPage");
const PartnersPage = page(() => import("./features/partners/PartnersPage"), "PartnersPage");
const PartnerPage = page(() => import("./features/partners/PartnerPage"), "PartnerPage");
const ReferralsPage = page(() => import("./features/referrals/ReferralsPage"), "ReferralsPage");
const ObjectivesPage = page(() => import("./features/objectives/ObjectivesPage"), "ObjectivesPage");
const CampaignPage = page(() => import("./features/objectives/CampaignPage"), "CampaignPage");
const LibraryPage = page(() => import("./features/library/LibraryPage"), "LibraryPage");
const EmailPage = page(() => import("./features/email/EmailPage"), "EmailPage");
const TemplateEditorPage = page(() => import("./features/email/TemplateEditorPage"), "TemplateEditorPage");
const SendPage = page(() => import("./features/email/SendPage"), "SendPage");
const LinksPage = page(() => import("./features/links/LinksPage"), "LinksPage");
const InsightsPage = page(() => import("./features/insights/InsightsPage"), "InsightsPage");
const DashboardPage = page(() => import("./features/insights/DashboardPage"), "DashboardPage");
const PreferenceCentrePage = page(() => import("./features/public/PreferenceCentrePage"), "PreferenceCentrePage");
const UnsubscribePage = page(() => import("./features/public/UnsubscribePage"), "UnsubscribePage");

export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      {/* For the people we email: no sign-in, authorised by the token in the link. */}
      <Route path="/p/:token" element={<Lazy el={<PreferenceCentrePage />} />} />
      <Route path="/u/:token" element={<Lazy el={<UnsubscribePage />} />} />
      <Route
        element={
          <AuthGate>
            <Shell />
          </AuthGate>
        }
      >
        <Route index element={<Lazy el={<HomePage />} />} />
        <Route path="calendar" element={<Lazy el={<CalendarPage />} />} />
        <Route path="prospects" element={<Lazy el={<RegistryPage />} />} />
        <Route path="prospects/:id" element={<Lazy el={<OrganisationPage />} />} />
        {/* Prospects was called the Registry; keep old links working. */}
        <Route path="registry/*" element={<Moved from="/registry" to="/prospects" />} />
        <Route path="contacts" element={<Lazy el={<ContactsPage />} />} />
        <Route path="contacts/:id" element={<Lazy el={<ContactPage />} />} />
        <Route path="pipeline" element={<Lazy el={<PipelinePage />} />} />
        <Route path="deals/:id" element={<Lazy el={<DealPage />} />} />
        <Route path="customers" element={<Lazy el={<AccountsPage />} />} />
        {/* The Customers screen was called Accounts; keep old links working. */}
        <Route path="accounts" element={<Moved from="/accounts" to="/customers" />} />
        <Route path="admin/*" element={<Lazy el={<AdminPage />} />} />
        <Route path="partners" element={<Lazy el={<PartnersPage />} />} />
        <Route path="partners/:id" element={<Lazy el={<PartnerPage />} />} />
        <Route path="referrals" element={<Lazy el={<ReferralsPage />} />} />
        <Route path="objectives" element={<Lazy el={<ObjectivesPage />} />} />
        <Route path="campaigns/:id" element={<Lazy el={<CampaignPage />} />} />
        <Route path="library" element={<Lazy el={<LibraryPage />} />} />
        <Route path="email" element={<Lazy el={<EmailPage />} />} />
        <Route path="email/templates/:id" element={<Lazy el={<TemplateEditorPage />} />} />
        <Route path="email/sends/:id" element={<Lazy el={<SendPage />} />} />
        <Route path="links" element={<Lazy el={<LinksPage />} />} />
        <Route path="insights" element={<Lazy el={<InsightsPage />} />} />
        <Route path="insights/:id" element={<Lazy el={<DashboardPage />} />} />
        <Route path="*" element={<EmptyState title="Page not found" />} />
      </Route>
    </Routes>
  );
}

/** Redirect a renamed screen, keeping the rest of the path and the query. */
function Moved({ from, to }: { from: string; to: string }) {
  const { pathname, search } = useLocation();
  return <Navigate to={{ pathname: to + pathname.slice(from.length), search }} replace />;
}

function Lazy({ el }: { el: ReactElement }) {
  return <Suspense fallback={<Loading />}>{el}</Suspense>;
}
